import {
    addMember,
    addPlayer,
    cashOutPlayer as cashOutPlayerRequest,
    createGame,
    createTransaction,
    deleteGame,
    deleteTransaction,
    fetchGame,
    removePlayer,
    settleGame as settleGameRequest,
    updateMember,
    updateTransaction
} from "../services/ledgerService";
import { actionTypes } from "./houseActionTypes";
import { buildCashOutRequest } from "./cashOut";

export default function createHouseDispatch(state, dispatch) {
    return {
        loadGames: async (gameIds) => {
            try {
                const games = await Promise.all(gameIds.map(id => fetchGame({ gameId: id })));
                dispatch({ type: actionTypes.LOAD_MULTIPLE_GAMES, payload: { games }});
            } catch (e) {
                dispatch({
                    type: actionTypes.LOAD_MULTIPLE_GAMES + '_FAILURE',
                    payload: { error: e.message }
                });
            }
        },
        loadGame: async (gameId) => {
            try {
                const game = await fetchGame({ gameId });
                dispatch({ type: actionTypes.LOAD_GAME, payload: game })
            } catch (error) {
                dispatch({ type: actionTypes.LOAD_GAME + '_FAILURE', payload: { error: error.message }});
                throw error;
            }
        },
        deleteGame: async (gameId) => {
            try {
                await deleteGame(gameId);
                dispatch({ type: actionTypes.DELETE_GAME, payload: { gameId }});
            } catch (e) {
                throw e;
            }
        },
        addMember: async (name) => {
            try {
                const member = await addMember({ name });
                dispatch({ type: actionTypes.ADD_MEMBER, payload: { member }});
                return member;
            } catch (e) {
                dispatch({ 
                    type: actionTypes.ADD_MEMBER + '_FAILURE',
                    payload: { error: e.message },
                });
                throw e;
            }
        },
        updateMember: async (member) => {
            try {
                await updateMember(member.id, member.name);
                dispatch({ type: actionTypes.UPDATE_MEMBER, payload: { member }});
            } catch (e) {
                dispatch({ 
                    type: actionTypes.UPDATE_MEMBER + '_FAILURE',
                    payload: { error: e.message },
                });
            }
        },
        addGame: async (game) => {
            try {
                const newGame = await createGame(game);
                dispatch({ type: actionTypes.ADD_GAME, payload: newGame });
            } catch (e) {
                throw e;
            }
        },
        addPlayer: async (gameId, memberId) => {
            dispatch({ type: actionTypes.ADD_PLAYER_REQUEST });
            try {
                const player = await addPlayer({ gameId, memberId });
                dispatch({ type: actionTypes.ADD_PLAYER_SUCCESS, payload: { gameId, player }});
            } catch (e) {
                dispatch({ type: actionTypes.ADD_PLAYER_FAILURE, payload: { error: e.message }});
            }
        },
        removePlayer: async (gameId, playerId) => {
            dispatch({ type: actionTypes.REMOVE_PLAYER_REQUEST });
            try {
                await removePlayer({ gameId, playerId });
                dispatch({ type: actionTypes.REMOVE_PLAYER_SUCCESS, payload: { gameId, playerId }});
            } catch (e) {
                dispatch({ type: actionTypes.REMOVE_PLAYER_FAILURE});
            }
        },
        updatePendingTransaction: async (gameId, transaction) => {
            dispatch({ type: actionTypes.UPDATE_PENDING_TRANSACTION, payload: { gameId, pendingTransaction: transaction }})
        },
        createTransaction: async (gameId, playerId, transaction) => {
            dispatch({ type: actionTypes.CREATE_TRANSACTION_REQUEST });
            try {
                const txn = await createTransaction({ gameId, playerId, transaction });
                dispatch({ type: actionTypes.CREATE_TRANSACTION_SUCCESS, payload: { gameId, playerId, transaction: txn }});
                dispatch({ type: actionTypes.UPDATE_PENDING_TRANSACTION, payload: { gameId, pendingTransaction: {
                            ...transaction,
                            paymentType: 'CASH',
                            isSettled: true,
                            transactionType: 'BUY_IN',
                        }
                    }});
                return { error: null }
            } catch (e) {
                dispatch({ type: actionTypes.CREATE_TRANSACTION_FAILURE });
                return { error: e.message }
            }
        },
        createCashOutTransactions: async (gameId, playerId, transaction) => {
            dispatch({ type: actionTypes.CREATE_TRANSACTION_REQUEST });

            const player = state.games[gameId].players.find(p => p.id === playerId)
            const pendingCoverTxnIds = player?.pendingCoverTransactionIds || [];
            const coveredTxns = player?.transactions.filter(t => pendingCoverTxnIds.includes(t.id));

            for (const t of coveredTxns) {
                try {
                    const coverTransaction = await createTransaction({
                        gameId,
                        playerId,
                        transaction: { amount: t.amount, isSettled: true, type: 'cover', paymentType: t.paymentType },
                    });
                    dispatch({ type: actionTypes.CREATE_COVER_TRANSACTION, payload: { gameId, playerId, transaction: coverTransaction }});

                    const updatedTransaction = await updateTransaction({ gameId, transaction: { ...t, isSettled: true }});
                    dispatch({ type: actionTypes.TOGGLE_TRANSACTION_STATUS, payload: { gameId, playerId, transaction: updatedTransaction }})
                } catch (e) {
                    dispatch({ type: actionTypes.CREATE_TRANSACTION_FAILURE });
                    return { error: e.message }
                }
            }
            try {
                const txn = await createTransaction({ gameId, playerId, transaction });
                dispatch({ type: actionTypes.CREATE_TRANSACTION_SUCCESS, payload: { gameId, playerId, transaction: txn }});
                dispatch({ type: actionTypes.CLEAR_COVER_CANDIDATES, payload: { gameId, playerId }});
                dispatch({ type: actionTypes.UPDATE_PENDING_TRANSACTION, payload: { gameId, pendingTransaction: {
                            ...transaction,
                            paymentType: 'CASH',
                            isSettled: true,
                            transactionType: 'BUY_IN',
                        }
                    }});
                return { error: null }
            } catch (e) {
                dispatch({ type: actionTypes.CREATE_TRANSACTION_FAILURE });
                return { error: e.message }
            }
        },
        // Guided cash-out. The API owns the business logic: it creates a
        // `cover` for each buy-in being paid down (settling the buy-in when the
        // cover fully covers it), then records the leftover stack as CASH_OUT
        // payouts. `covers` is [{ transaction, amount }]; `payouts` is
        // [{ paymentType, amount, isSettled }]. We send one request and re-fetch
        // the game so the new transactions and any settled buy-ins are reflected.
        cashOutPlayer: async (gameId, playerId, { covers = [], payouts = [] }) => {
            dispatch({ type: actionTypes.CREATE_TRANSACTION_REQUEST });
            try {
                const body = buildCashOutRequest({ covers, payouts });
                await cashOutPlayerRequest({ gameId, playerId, ...body });

                const game = await fetchGame({ gameId });
                dispatch({ type: actionTypes.LOAD_MULTIPLE_GAMES, payload: { games: [game] }});
                return { error: null };
            } catch (e) {
                dispatch({ type: actionTypes.CREATE_TRANSACTION_FAILURE });
                return { error: e.message };
            }
        },
        // Close the books: settle every outstanding transaction, then re-fetch
        // the game so the cleared balances are reflected.
        settleGame: async (gameId) => {
            try {
                await settleGameRequest({ gameId });
                const game = await fetchGame({ gameId });
                dispatch({ type: actionTypes.LOAD_MULTIPLE_GAMES, payload: { games: [game] }});
                return { error: null };
            } catch (e) {
                return { error: e.message };
            }
        },
        deleteTransaction: async (gameId, playerId, transaction) => {
            dispatch({ type: actionTypes.DELETE_TRANSACTION_REQUEST });
            try {
                await deleteTransaction(gameId, transaction.id);
                dispatch({ type: actionTypes.DELETE_TRANSACTION_SUCCESS, payload: { gameId, playerId, transaction }});
            } catch (e) {
                dispatch({ type: actionTypes.DELETE_TRANSACTION_FAILURE });
            }
        },
        toggleTransactionStatus: async (gameId, playerId, transaction) => {
            dispatch({ type: actionTypes.TOGGLE_TRANSACTION_STATUS + '_REQUEST' })
            try {
                const updatedTransaction = await updateTransaction({ gameId, transaction });
                dispatch({ type: actionTypes.TOGGLE_TRANSACTION_STATUS, payload: { gameId, playerId, transaction: updatedTransaction }})
            } catch (e) {
                dispatch({ type: actionTypes.TOGGLE_TRANSACTION_STATUS + '_FAILED' })
            }
        },
        changeTransaction: async (gameId, playerId, transaction) => {
            dispatch({ type: actionTypes.CHANGE_TRANSACTION_PAYMENT_TYPE + '_REQUEST' })
            try {
                const updatedTransaction = await updateTransaction({ gameId, transaction });
                dispatch({ type: actionTypes.CHANGE_TRANSACTION_PAYMENT_TYPE, payload: { gameId, playerId, transaction: updatedTransaction }})
            } catch (e) {
                dispatch({ type: actionTypes.CHANGE_TRANSACTION_PAYMENT_TYPE + '_FAILED' })
            }
        },
        updateCoverTransactions: async (gameId, playerId, transaction) => {
            const action = !transaction.isSettled
                ? actionTypes.ADD_COVER_CANDIDATE
                : actionTypes.REMOVE_COVER_CANDIDATE;

            const pendingTransaction = state.games[gameId].pendingTransaction;

            if (transaction.amount > pendingTransaction.amount && action === actionTypes.ADD_COVER_CANDIDATE) {
                return { error: "the pending transaction cannot cover the unsettled transaction" };
            }

            dispatch({ type: action, payload: { gameId, playerId, transaction: transaction }})

            return { error: null };
        },
        resetCoverTransactions: async (gameId, playerId) => {
            dispatch({ type: actionTypes.RESET_COVER_CANDIDATE, payload: { gameId, playerId }});
        },
        sortPlayers: (gameId, sortBy, sortOrder) => {
            dispatch({ type: actionTypes.SORT_PLAYERS, payload: { gameId, sortBy, sortOrder }})
        },
    };
}
